import { useLayoutEffect, useState } from 'react';

/**
 * Resolves a DOM element by id for use as a React portal target.
 *
 * Naively doing `document.getElementById(...)` inside a single empty-dep
 * `useEffect` races against the mount order of the slot owner (e.g. AppLayout
 * renders its footer slot AFTER children in source order, so when a child
 * component mounts and runs its effect, the slot may not yet be in the DOM).
 * If the lookup returns `null` on that first probe, a naive implementation
 * keeps `null` forever and the portal silently never renders.
 *
 * This hook:
 *   1. Probes synchronously via `useLayoutEffect` (covers the common case
 *      where the slot is already painted).
 *   2. If absent, schedules a `requestAnimationFrame` retry loop, capped at
 *      `maxFrames` frames (~1s at 60fps) so we don't leak a runaway loop if
 *      the slot never appears.
 *
 * @param id The id attribute of the slot element.
 * @param maxFrames Maximum number of rAF retries before giving up. Default 60.
 * @returns The resolved element, or null if not yet found / not present.
 */
export function usePortalSlot(
  id: string,
  maxFrames: number = 60,
): HTMLElement | null {
  const [slot, setSlot] = useState<HTMLElement | null>(null);

  useLayoutEffect(() => {
    const initial = document.getElementById(id);
    if (initial) {
      setSlot(initial);
      return () => setSlot(null);
    }

    let frame = 0;
    let rafId: number | null = null;
    let cancelled = false;

    const probe = () => {
      if (cancelled) return;
      const el = document.getElementById(id);
      if (el) {
        setSlot(el);
        return;
      }
      if (frame++ < maxFrames) {
        rafId = requestAnimationFrame(probe);
      }
    };

    rafId = requestAnimationFrame(probe);

    return () => {
      cancelled = true;
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      setSlot(null);
    };
  }, [id, maxFrames]);

  return slot;
}
